
import { createContext, useContext, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { analyticsService } from '@/services/analytics/AnalyticsService';
import { useExtensionContentMonitoring } from '@/hooks/useExtensionContentMonitoring';

interface AnalyticsContextType {
  trackEvent: (eventType: string, eventCategory: string, eventData?: Record<string, any>) => void;
  trackPageView: (pagePath?: string) => void;
  trackUserAction: (action: string, details?: Record<string, any>) => void;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

export function AnalyticsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();

  // Initialize extension content monitoring
  useExtensionContentMonitoring();

  // Track page views automatically as navigation events (not user_action)
  useEffect(() => {
    analyticsService.trackEvent('page_view', 'navigation', {
      page: location.pathname,
      referrer: document.referrer
    }, user);
  }, [location, user]);

  // Update user state when auth changes
  useEffect(() => {
    analyticsService.updateUserState(user);
  }, [user]);

  const trackEvent = (
    eventType: string,
    eventCategory: string,
    eventData?: Record<string, any>
  ) => {
    analyticsService.trackEvent(eventType, eventCategory, eventData, user);
  };

  const trackPageView = (pagePath?: string) => {
    trackEvent('page_view', 'navigation', {
      page: pagePath || location.pathname,
      referrer: document.referrer
    });
  };

  const trackUserAction = (action: string, details?: Record<string, any>) => {
    trackEvent(action, 'user_action', details);
  };

  return (
    <AnalyticsContext.Provider value={{
      trackEvent,
      trackPageView,
      trackUserAction
    }}>
      {children}
    </AnalyticsContext.Provider>
  );
}

export function useAnalytics() {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
}
